import chroma from "chroma-js";

const cssColorMatcher = /(#([\da-f]{3}){1,2}|(rgb|hsl)a\((\d{1,3}%?,\s?){3}(1|0?\.\d+)\)|(rgb|hsl)\(\d{1,3}%?(,\s?\d{1,3}%?){2}\))/gi;

export function isDark(color) {
    const [r, g, b] = chroma(color).rgb();
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128;
}

function extractFontWeight(value) {
    switch (value) {
        case "100italic":
            return "100";
        case "200italic":
            return "200";
        case "300italic":
            return "300";
        case "regular":
        case "italic":
            return "400";
        case "500italic":
            return "500";
        case "600italic":
            return "600";
        case "700italic":
            return "700";
        case "800italic":
            return "800";
        case "900italic":
            return "900";
        default:
            return value;
    }
}

function adjustColorIntensity(color, intensityPercentage) {
    // Convert intensity percentage to decimal
    const intensity = intensityPercentage / 100;

    // Extract RGB components from the color
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // Adjust the intensity of each component
    const newR = Math.round(r * intensity);
    const newG = Math.round(g * intensity);
    const newB = Math.round(b * intensity);

    // Convert back to hexadecimal and ensure 2 digits
    const adjustedColor = `#${newR.toString(16).padStart(2, "0")}${newG
        .toString(16)
        .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;

    return adjustedColor;
}

export function alpha(color, intensity = 1) {
    return chroma(color).alpha(intensity).css();
}

export function alphaByPercentage(color: string, percent?: string) {
    if (!percent) {
        return color;
    }

    return adjustColorIntensity(color, percent);
}

export function darken(color, intensity = 1) {
    return chroma(color).darken(intensity).css();
}

export function brighten(color, intensity = 1) {
    return chroma(color).brighten(intensity).css();
}

export function foregroundColor(theme, background) {
    return isDark(background) ? theme.ForegroundColorDark : theme.ForegroundColorLight;
}

export function backgroundColor(theme) {
    const colors = theme.PageBackground.match(cssColorMatcher);
    return colors ? chroma.average(colors, "rgb").css() : "#ffffff";
}

export function wrapperBackgroundColor(theme) {
    const bgColor = backgroundColor(theme);
    const pageWrapperAlpha = chroma(theme.PageWrapperBackground).alpha();
    const pageWrapperColor = chroma(theme.PageWrapperBackground).alpha(0);
    return chroma.mix(bgColor, pageWrapperColor, pageWrapperAlpha, "rgb");
}

export function compoundBackgroundColor(theme, color) {
    const bgColor = wrapperBackgroundColor(theme);
    return chroma.mix(bgColor, color, theme.ControlAlpha, "rgb");
}

export function buttonBackgroundColor(theme) {
    return compoundBackgroundColor(theme, theme.ButtonBackground);
}

export function controlBackgroundColor(theme) {
    return compoundBackgroundColor(theme, theme.ControlBackground);
}

export function inputBackgroundColor(theme, focused?) {
    let color;

    if (
      theme.BorderWidth === 0 &&
      wrapperBackgroundColor(theme).hex("rgb") === chroma(theme.InputBackground).hex("rgb")
    ) {
        color = focused
          ? darken(theme.ControlBackground, theme.ActiveHighlightValue)
          : theme.ControlBackground;
    } else {
        color = theme.InputBackground;
    }

    return alpha(color, theme.ControlAlpha);
}

export function borderColor(theme, color) {
    return darken(color, theme.FieldBorderValue);
}

function inputWidth(theme, key, size, defaultSize) {
    const sizeKey = size || defaultSize;
    if (!sizeKey) {
        return undefined;
    }
    const width = theme[key][sizeKey];
    if (width) {
        return width;
    }
    return sizeKey;
}

export function inputMinWidth(theme, size, defaultSize = undefined) {
    return inputWidth(theme, "InputMinWidths", size, defaultSize);
}

export function inputMaxWidth(theme, size, defaultSize = undefined) {
    return inputWidth(theme, "InputMaxWidths", size, defaultSize);
}

export function getFontWeight(fontVariant, defaultWeight) {
    if (!fontVariant) {
        return defaultWeight;
    }
    return extractFontWeight(fontVariant);
}

export function getFontStyle(fontVariant) {
    if (!fontVariant) {
        return "";
    }
    return fontVariant.includes("italic") ? "italic" : "normal";
}

export function getFontSize(fontSize, defaultSize = "") {
    if (!fontSize) {
        return defaultSize;
    }
    return `${fontSize}px`;
}

export function getLineHeight(fontSize, defaultHeight, baseFontSize) {
    if (!fontSize) {
        return `${defaultHeight}px`;
    }
    if (parseInt(defaultHeight, 10) > 0) {
        return `${fontSize * (defaultHeight / baseFontSize)}px`;
    }
    return `${fontSize}px`;
}

export function getRowCornerBorderRadius(borderRadius: number): number {
    return borderRadius > 0 ? Math.round(Math.log(borderRadius) / Math.log(1.2)) : 0;
}

export function getTextAlign(align) {
    if (!align) {
        return "";
    }
    return align;
}

export function backgroundless(theme) {
    return theme.ControlAlpha === 0 && theme.BorderWidth === 0;
}

export function getButtonChoiceMargin(theme, horizontal) {
    if (horizontal) {
        const margin =
          getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents) / 2;
        return [[0, margin, margin, 0]];
    }
    return backgroundless(theme)
      ? [[0, -13]]
      : [
          [
              getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents),
              0,
              getSpacing(theme, theme.ChoiceSpacing, 0),
              0,
          ],
      ];
}

export function progressBarValueColor(theme, value) {
    if (theme.ProgressBarHeight < theme.ProgressBarFontSize) {
        return foregroundColor(theme, wrapperBackgroundColor(theme));
    }
    if (value > 50) {
        return foregroundColor(theme, theme.PrimaryColor);
    }
    if (theme.ProgressBarType !== "floating") {
        return foregroundColor(theme, controlBackgroundColor(theme));
    }
    return foregroundColor(theme, wrapperBackgroundColor(theme));
}

export function progressBarVerticalPosition(theme, simulatedBannerVisible?: boolean): number {
    if (theme.ProgressBarType !== "floating") {
        return null;
    }
    return simulatedBannerVisible ? 38 : 0;
}

// The function checks the theme.QuestionSpacing property
// We changed this propery to 'object' in the new feature
// if it is a number, the survey was created before feature
// returns old value if it is old
export function getSpacing(theme, oldValue, newValue) {
    const spacing = theme.QuestionSpacing;
    if (typeof spacing === "number") {
        return oldValue;
    }
    return newValue;
}
